<script lang="ts">
  import { onDestroy } from 'svelte';
  import { get } from 'svelte/store';
  import { queue, activeTab, type QueueItem } from '../lib/store.js';
  import { parseTlck, decryptFromTlck, initialStateFor } from '../lib/tlck.js';
  import { watchLock, getReleaseKey, requestChallenge, submitRelease } from '../lib/api.js';
  import { decodeKeyFile, importOwnerPrivkey, signRelease } from '../lib/owner.js';
  import { hexdec } from '../lib/bytes.js';
  import { CAN_SHARE_FILES, shareBlobUrl, setDragDownloadUrl } from '../lib/platform.js';
  import { t } from '../lib/i18n.js';

  let dragging = $state(false);
  let importError = $state('');

  // Release (key-file) state
  type ReleaseStep = 'idle' | 'confirm' | 'working' | 'done' | 'error';
  let releaseStep: ReleaseStep = $state('idle');
  let releaseMessage = $state('');
  let pendingKeyFile: File | null = $state(null);
  let releaseDismissTimer: ReturnType<typeof setTimeout> | undefined;

  function scheduleReleaseDismiss() {
    clearTimeout(releaseDismissTimer);
    releaseDismissTimer = setTimeout(() => { releaseStep = 'idle'; }, 10000);
  }

  // Clear release feedback immediately when switching away from this tab.
  $effect(() => {
    const unsub = activeTab.subscribe((tab) => {
      if (tab !== 'decrypt' && (releaseStep === 'done' || releaseStep === 'error')) {
        clearTimeout(releaseDismissTimer);
        releaseStep = 'idle';
      }
    });
    return unsub;
  });

  onDestroy(() => clearTimeout(releaseDismissTimer));

  const watchers = new Map<string, () => void>();

  function attachWatcher(item: QueueItem) {
    if (watchers.has(item.id)) return;
    const lockId = item.parsed.header.lock_id;
    if (!lockId) return;
    if (item.parsed.header.policy === 'time') return;
    const unsub = watchLock(lockId, async () => {
      const releasePriv = await getReleaseKey(lockId);
      const current = get(queue).find((x) => x.id === item.id);
      if (!current) return;
      if (current.state === 'done' || current.state === 'error' || current.state === 'decrypting') return;
      queue.updateItem(item.id, releasePriv ? { state: 'ready', releasePriv } : { state: 'ready' });
    });
    watchers.set(item.id, unsub);
  }

  function ensureWatchersFor(items: QueueItem[]) {
    for (const it of items) {
      if (it.state === 'waiting' || it.state === 'waiting-manual') attachWatcher(it);
    }
    for (const [id, unsub] of watchers) {
      const it = items.find((x) => x.id === id);
      if (!it || it.state === 'done' || it.state === 'error') {
        unsub();
        watchers.delete(id);
      }
    }
  }

  $effect(() => {
    const unsub = queue.subscribe(ensureWatchersFor);
    return unsub;
  });

  onDestroy(() => {
    for (const unsub of watchers.values()) unsub();
    watchers.clear();
  });

  let now = $state(Date.now());
  const timer = setInterval(() => { now = Date.now(); }, 1000);
  onDestroy(() => clearInterval(timer));

  function translateError(msg: string): string {
    const key = `errors.${msg}`;
    const translated = $t(key);
    return translated !== key ? translated : msg;
  }

  async function handleKeyFile(f: File) {
    releaseStep = 'working';
    releaseMessage = '';
    try {
      const text = await f.text();
      const decoded = decodeKeyFile(text);
      if (!decoded) {
        releaseStep = 'error';
        releaseMessage = $t('decrypt.release_invalid_key');
        return;
      }
      const ownerPriv = await importOwnerPrivkey(decoded.pkcs8Priv);
      const { nonce } = await requestChallenge(decoded.lockIdHex);
      const lockIdBytes = hexdec(decoded.lockIdHex);
      const signature = await signRelease(ownerPriv, lockIdBytes, nonce);
      const releasePriv = await submitRelease(decoded.lockIdHex, nonce, signature);

      for (const item of get(queue)) {
        if (item.parsed.header.lock_id === decoded.lockIdHex) {
          queue.updateItem(item.id, { releasePriv, state: 'ready' });
        }
      }

      releaseStep = 'done';
      releaseMessage = $t('decrypt.release_success');
      scheduleReleaseDismiss();
    } catch {
      releaseStep = 'error';
      releaseMessage = $t('decrypt.release_failed');
      scheduleReleaseDismiss();
    }
    pendingKeyFile = null;
  }

  function confirmRelease() {
    if (pendingKeyFile) handleKeyFile(pendingKeyFile);
  }

  function cancelRelease() {
    releaseStep = 'idle';
    pendingKeyFile = null;
  }

  async function handleFiles(files: FileList | File[]) {
    importError = '';
    for (const f of Array.from(files)) {
      if (f.name.endsWith('.tlck-key')) {
        pendingKeyFile = f;
        releaseStep = 'confirm';
        releaseMessage = '';
        continue;
      }
      try {
        const bytes = new Uint8Array(await f.arrayBuffer());
        const parsed = await parseTlck(bytes);
        let state = initialStateFor(parsed.header);
        let releasePriv: Uint8Array | undefined;

        const lockId = parsed.header.lock_id;
        const isManualCapable =
          parsed.header.policy === 'manual' || parsed.header.policy === 'time_manual';
        if (lockId && isManualCapable) {
          const key = await getReleaseKey(lockId);
          if (key) {
            releasePriv = key;
            state = 'ready';
          }
        }

        queue.add({
          id: crypto.randomUUID(),
          file: f,
          tlckBytes: bytes,
          parsed,
          state,
          releasePriv,
          addedAt: Date.now(),
        });
      } catch {
        importError = $t('decrypt.import_error', { name: f.name });
      }
    }
  }

  function onPickFiles(ev: Event) {
    const input = ev.currentTarget as HTMLInputElement;
    if (input.files) handleFiles(input.files);
  }

  function onDrop(ev: DragEvent) {
    ev.preventDefault();
    dragging = false;
    if (ev.dataTransfer?.files) handleFiles(ev.dataTransfer.files);
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  function policyLabel(p: string): string {
    if (p === 'time') return $t('decrypt.policy_time');
    if (p === 'time_manual') return $t('decrypt.policy_time_manual');
    if (p === 'manual') return $t('decrypt.policy_manual');
    return p;
  }

  function formatCountdown(ms: number): string {
    if (ms <= 0) return '00:00:00';
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return days > 0
      ? `${days}d ${pad(h)}:${pad(m)}:${pad(s)}`
      : `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  function timeReadyFor(item: QueueItem): boolean {
    const h = item.parsed.header;
    if (!h.unlock_unix) return false;
    return now >= h.unlock_unix * 1000;
  }

  $effect(() => {
    void now;
    for (const it of get(queue)) {
      if (it.state === 'waiting' && timeReadyFor(it)) {
        queue.updateItem(it.id, { state: 'ready' });
      }
    }
  });

  async function decryptItem(item: QueueItem) {
    queue.updateItem(item.id, { state: 'decrypting', error: undefined });
    try {
      const plain = await decryptFromTlck({
        parsed: item.parsed,
        releasePriv: item.releasePriv,
      });
      const blob = new Blob([plain as BlobPart], { type: item.parsed.header.orig_mime || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      queue.updateItem(item.id, {
        state: 'done',
        decryptedBlobUrl: url,
        decryptedSize: plain.length,
      });
    } catch (e) {
      queue.updateItem(item.id, {
        state: 'error',
        error: e instanceof Error ? translateError(e.message) : $t('decrypt.decrypt_failed'),
      });
    }
  }

  function saveDecrypted(item: QueueItem) {
    if (!item.decryptedBlobUrl) return;
    const a = document.createElement('a');
    a.href = item.decryptedBlobUrl;
    a.download = item.parsed.header.orig_name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function downloadTlck(item: QueueItem) {
    const blob = new Blob([item.tlckBytes as BlobPart], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${item.parsed.header.orig_name}.tlck`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
</script>

<div class="col">
  <div class="step">
    <h3>{$t('decrypt.import_title')}</h3>
    <label
      class="dropzone"
      class:drag={dragging}
      ondragover={(e) => { e.preventDefault(); dragging = true; }}
      ondragleave={() => { dragging = false; }}
      ondrop={onDrop}
    >
      <input type="file" multiple accept=".tlck,.tlck-key" style="display:none" onchange={onPickFiles} />
      {$t('decrypt.dropzone')}
    </label>
    <p class="dim small" style="margin:0">{@html $t('decrypt.help')}</p>
    {#if importError}<div class="alert error">{importError}</div>{/if}

    {#if releaseStep === 'confirm' && pendingKeyFile}
      <div class="alert warn">
        {@html $t('decrypt.release_confirm', { name: pendingKeyFile.name })}
        <div class="row" style="margin-top:8px">
          <button class="warn" onclick={confirmRelease}>{$t('decrypt.release_btn_confirm')}</button>
          <button onclick={cancelRelease}>{$t('decrypt.release_btn_cancel')}</button>
        </div>
      </div>
    {:else if releaseStep === 'working'}
      <div class="alert warn"><span class="spin"></span>{$t('decrypt.release_working')}</div>
    {:else if releaseStep === 'done'}
      <div class="alert ok">{releaseMessage}</div>
    {:else if releaseStep === 'error'}
      <div class="alert error">{releaseMessage}</div>
    {/if}
  </div>

  <div class="step">
    <h3>{$t('decrypt.queue_title')}</h3>
    {#if $queue.length === 0}
      <div class="dim small">{$t('decrypt.queue_empty')}</div>
    {/if}
    {#each $queue as item (item.id)}
      <div
        class="queue-card"
        class:ready={item.state === 'ready'}
        class:done={item.state === 'done'}
        class:error={item.state === 'error'}
        class:waiting-manual={item.state === 'waiting-manual'}
      >
        <div class="qc-top">
          <span class="qc-name">{item.parsed.header.orig_name}</span>
          <span class="badge">{policyLabel(item.parsed.header.policy)}</span>
          <div class="qc-actions">
            {#if item.state === 'waiting' || item.state === 'waiting-manual'}
              <button
                class="icon-btn"
                title={$t('decrypt.title_download_tlck')}
                aria-label={$t('decrypt.title_download_tlck')}
                onclick={() => downloadTlck(item)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 4v12"/>
                  <path d="M8 12l4 4 4-4"/>
                  <path d="M5 20h14"/>
                </svg>
              </button>
            {/if}
            <button
              class="icon-btn"
              title={$t('decrypt.title_remove')}
              aria-label={$t('decrypt.aria_remove')}
              onclick={() => queue.remove(item.id)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 6l12 12"/>
                <path d="M18 6l-12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {#if item.state === 'waiting' && item.parsed.header.unlock_unix}
          <div class="countdown">
            {formatCountdown(item.parsed.header.unlock_unix * 1000 - now)}
          </div>
        {:else if item.state === 'waiting-manual'}
          <span class="badge warn">{$t('decrypt.badge_waiting_manual')}</span>
        {:else if item.state === 'ready'}
          <button class="primary" onclick={() => decryptItem(item)}>{$t('decrypt.btn_decrypt')}</button>
        {:else if item.state === 'decrypting'}
          <button class="primary" disabled><span class="spin"></span>{$t('decrypt.decrypting')}</button>
        {:else if item.state === 'done' && item.decryptedBlobUrl}
          {@const name = item.parsed.header.orig_name}
          {@const mime = item.parsed.header.orig_mime || 'application/octet-stream'}
          <div
            class="file-chip decrypted"
            role="group"
            aria-label={$t('decrypt.aria_decrypted_file', { name })}
            draggable="true"
            ondragstart={(e) => setDragDownloadUrl(e, item.decryptedBlobUrl, name, mime)}
            title={$t('decrypt.title_drag')}
          >
            <div class="file-chip-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
                <path d="M14 3v5h5"/>
                <path d="M9 15l2 2 4-4"/>
              </svg>
            </div>
            <div class="file-chip-body">
              <div class="file-chip-name">{name}</div>
              <div class="file-chip-meta">
                {$t('decrypt.decrypted')}{item.decryptedSize != null ? ` · ${formatBytes(item.decryptedSize)}` : ''}
              </div>
            </div>
            <div class="file-chip-actions">
              {#if CAN_SHARE_FILES}
                <button
                  class="icon-btn"
                  draggable="false"
                  title={$t('decrypt.title_share')}
                  aria-label={$t('decrypt.title_share')}
                  onclick={() => shareBlobUrl(item.decryptedBlobUrl!, name, mime)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="18" cy="5" r="3"/>
                    <circle cx="6" cy="12" r="3"/>
                    <circle cx="18" cy="19" r="3"/>
                    <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/>
                    <line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
                  </svg>
                </button>
              {/if}
              <button
                class="icon-btn"
                draggable="false"
                title={$t('decrypt.title_save')}
                aria-label={$t('decrypt.title_save')}
                onclick={() => saveDecrypted(item)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 4v12"/>
                  <path d="M8 12l4 4 4-4"/>
                  <path d="M5 20h14"/>
                </svg>
              </button>
            </div>
          </div>
        {:else if item.state === 'error'}
          <div class="alert error">{item.error ?? $t('decrypt.decrypt_failed')}</div>
          <div>
            <button onclick={() => decryptItem(item)}>{$t('decrypt.btn_retry')}</button>
          </div>
        {/if}
      </div>
    {/each}
  </div>
</div>
